import { useRef, useState } from 'react'
import { Camera, X, Loader, ZoomIn } from 'lucide-react'
import { Modal } from './Modal'

interface FotoUploadProps {
  /** Upload-Ordner, z.B. "geraete/abc123" oder "service/def456" */
  uploadFolder: string
  fotos: string[]
  onChange: (fotos: string[]) => void
  readOnly?: boolean
}

export function FotoUpload({ uploadFolder, fotos, onChange, readOnly }: FotoUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [viewFoto, setViewFoto] = useState<string | null>(null)
  const [error, setError] = useState('')

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    setUploading(true)
    setError('')
    const newFotos = [...fotos]
    for (const file of files) {
      try {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('folder', uploadFolder)
        const res = await fetch('/api/upload', { method: 'POST', body: fd })
        if (!res.ok) {
          const text = await res.text()
          throw new Error(text)
        }
        const { url } = await res.json() as { url: string }
        newFotos.push(url)
      } catch (err) {
        setError('Upload fehlgeschlagen: ' + (err as Error).message)
      }
    }
    onChange(newFotos)
    setUploading(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  const removeFoto = (url: string) => onChange(fotos.filter((f) => f !== url))

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {fotos.map((url) => (
          <div key={url} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 group">
            <img src={url} alt="Foto" className="w-full h-full object-cover" onClick={() => setViewFoto(url)} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => setViewFoto(url)} className="p-1 bg-white/90 rounded-lg text-gray-700">
                <ZoomIn size={14} />
              </button>
              {!readOnly && (
                <button type="button" onClick={() => removeFoto(url)} className="p-1 bg-white/90 rounded-lg text-red-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}

        {!readOnly && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all disabled:opacity-50"
          >
            {uploading ? <Loader size={20} className="animate-spin" /> : (
              <><Camera size={20} /><span className="text-xs">Foto</span></>
            )}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      <Modal open={!!viewFoto} onClose={() => setViewFoto(null)} title="Foto" size="lg">
        {viewFoto && (
          <img src={viewFoto} alt="Foto gross" className="w-full rounded-xl object-contain max-h-[60vh]" />
        )}
      </Modal>
    </div>
  )
}
